<template>
  <div class="message-files message-prescription"
       :title="datetime">

    <div class="message-files__not-image"
         v-if="!isImage">

      <p v-if="!showCoupon" class="label-6 m-b-5">Sent a Prescription.</p>

      <div class="smd-panel smd-form__row m-b-10"
           @click="viewFile">
        <span class="file-icon"
              :class="computeFileIcon"/>
        <p class="file-name">Prescription</p>
      </div>

      <template v-if="showCoupon">
        <div class="message-prescription__coupon m-b-15"
             :class="{'align-self-center': !$isDesktop}">
          <smd-coupon has-bottom-btn-label
                      class="m-b-15"
                      :class="couponClass"
                      :bottom-btn-style="{section: '7', variant: couponBtnVariant}"
                      :btn-label="couponLabel"
                      :key="coupon.couponid"
                      :item="coupon"
                      :small="!$isDesktop"
                      :title="coupon.shortname"
                      :image="couponImage"
                      @button-click="onCouponClick"/>
        </div>

        <coupon-countdown-timer class="m-b-25"
                                :class="{'align-self-center': !$isDesktop}"
                                :start-time="startTimeString"
                                :end-time="endTimeString"
                                :large="$isDesktop"
                                :trans="transConfig"/>

        <div class="smd-form__row m-b-5">
          <text-string label-id="txt238" :props="{strings: [coupon.shortname]}"/>
        </div>
      </template>

      <p class="Label1 gray-400">{{ formattedTime }}</p>
    </div>

    <a :href="img_url"
       target="_blank"
       class="message-files__image"
       v-if="isImage">
      <div v-if="loading" class="loading-spinner">
        <smd-loading-spinner></smd-loading-spinner>
      </div>
      <img v-if="img_url"
           :src="img_url"
           @load="imageLoaded">
      <p v-if="error" class="error">Preview not found.</p>
      <p class="text"><span :class="'ns-icon-attachment'"/> {{ fileNameString }}</p>
      <p class="gray-400 time">{{ formattedTime }}</p>
    </a>
  </div>
</template>

<script>
  import {MIME_TYPES, MESSAGE_PROP_MSG_TYPE} from '@/constants/inbox-messages';
  import {FilesService} from '@/services/files';
  import moment from 'moment';
  import Routes from "@/router/routes";

  import PreviewFileType from "@/reference/preview-file-type";
  // import {
  //   COUPON_STATUS,
  // } from "@/reference/medicine";

  import CouponCountdownTimer from '@/components/coupon-countdown-timer';

  import CountdownStartEndTimeMixin from '@/mixins/services/countdown-start-end-time-mixin';
  import TitleMixin from '@/mixins/title-mixin';
  import GetElementString from "@/mixins/get-element-string";

  import SanofiTryitLogo from "@/assets/images/img010.png";
  import {mapGetters, mapMutations} from "vuex";
  import {GETTER_DOCTOR_PATIENT_PARTNER, MUTATE_ADD_ATTACHED_PRESCRIPTION} from "@/store/types";
  import {
    couponExpirationDate,
    isCouponExpired,
    canAvailCoupon,
    getPrescriptionCoupon,
  } from "@/utils/medicine";
  import {
    getCouponLogo,
  } from '@/reference/coupon';

  export default {
    name: "message-prescription",
    mixins: [
      TitleMixin,
      GetElementString,
      CountdownStartEndTimeMixin
    ],
    components: {CouponCountdownTimer},
    props: {
      message: {type: Object, required: true},
      token: {type: String, required: true},
    },
    data() {
      const coupon = getPrescriptionCoupon(this.message);

      return {
        error: null,
        img_url: null,
        loading: false,
        transConfig: {
          "days": "Days",
          "hours": "Hrs",
          "minutes": "Mins",
          "seconds": "Secs",
        },
        coupon,
        canAvailCoupon: canAvailCoupon(coupon),
        couponIsExpired: isCouponExpired(coupon),
        couponExpirationDate: couponExpirationDate(coupon),
      }
    },
    computed: {
      ...mapGetters({
        doctorPatientPartner: GETTER_DOCTOR_PATIENT_PARTNER,
      }),
      datetime() {
        return moment(this.message.received).format('MMMM D, YYYY hh:mm a');
      },
      computeFileIcon() {
        return this.message.mimetype === MIME_TYPES.pdf ? 'ns-icon-ico012' : 'ns-icon-ico014';
      },
      isImage() {
        const imageTypes = [MIME_TYPES.png, MIME_TYPES.jpg, MIME_TYPES.jpeg]
        return imageTypes.includes(this.message.mimetype);
      },
      fileNameString() {
        return this.message[MESSAGE_PROP_MSG_TYPE.file];
      },
      formattedTime() {
        return moment(this.message.received).format('hh:mm a');
      },
      pageType() {
        return this.message.pagetype;
      },
      showCoupon() {
        // return !!this.coupon && this.coupon.status !== COUPON_STATUS.ARCHIVED;
        return false;
      },
      couponClass() {
        return [
          {'message-prescription__coupon--expired': this.couponIsExpired},
          {'message-prescription__coupon--claimed': !this.canAvailCoupon},
        ];
      },
      couponBtnVariant() {
        return this.canAvailCoupon ? 'red' : 'blue';
      },
      couponLabel() {
        if (!this.canAvailCoupon) return this.getElementString('txt1144');
        if (this.couponIsExpired) return this.getElementString('txt1145');
        return this.getElementString('txt339', {strings: [this.couponExpirationDate.format('MMMM D, YYYY')]});
      },
      couponImage() {
        return getCouponLogo(this.coupon);
      },
    },
    mounted() {
      if (this.isImage) {
        this.fetchFileImg();
      }
    },
    methods: {
      ...mapMutations({
        addPrescription: MUTATE_ADD_ATTACHED_PRESCRIPTION
      }),
      async goToFile() {
        this.error = null;
        try {
          const {data} = await FilesService.getFileURL(this.token, this.message.uploadid);
          if (!data) {
            throw Error();
          }
          window.open(`${data.url}`, '_blank');
        } catch (e) {
          this.error = 'An unexpected error occured on viewing your file.';
          this.$emit('file-view-error', this.error);
        }
      },
      async fetchFileImg() {
        this.error = null;
        this.loading = true;
        try {
          const {data} = await FilesService.getFileURL(this.token, this.message.uploadid);
          if (!data) {
            throw Error();
          }

          this.loading = false;
          this.img_url = data.url;
        } catch (e) {
          this.loading = false;
          this.error = 'An unexpected error occured on fetching the image.';
          this.$emit('file-img-error', this.error);
        }
      },
      onCouponClick() {
        if (this.couponIsExpired) return;

        const {doctor, patient} = this.doctorPatientPartner[this.token];
        const prescription = {
          ...this.message,
          partial: false,
          includeOtherItems: true,
          remarks: '',
          doctorname: doctor._fullName,
          patientname: patient.name,
        };

        this.addPrescription(prescription);
        this.$router.push({
          name: Routes.PHARMACY_ORDERS,
        });
      },
      viewFile() {
        const {doctor} = this.doctorPatientPartner[this.token];
        const fileData = {...this.message, token: this.token, doctorname: doctor._fullName}
        this.$router.push(
          {
            name: Routes.PREVIEW_FILE,
            params: {
              file: [fileData],
              title: this.computedTitle(fileData),
              filesView: true,
              type: PreviewFileType.PREVIEW_TYPE_PRESCRIPTION
            },
          }
        )
      },
      imageLoaded() {
        this.$emit('loaded');
      },
    },
    SanofiTryitLogo
  }
</script>

<style lang="scss">
  @import "~@/assets/styles/util.scss";

  .message-prescription {
    max-width: 372px;
    width: 100%;

    .smd-panel {
      padding: space(xs) space(s);
    }
  }

  .message-prescription__coupon {
    .smd-coupon__content {
      .smd-coupon__action-btn {
        display: none;
      }
    }

    .smd-coupon__bottom-action-btn {
      animation: beat .20s infinite alternate;
    }

    &--expired,
    &--claimed {
      .smd-coupon__bottom-action-btn {
        animation: none;
        opacity: 0.5;
      }
    }
  }
</style>